import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { formatCompactNumber } from "app/pages/sales-intelligence/utils/numbers";

import type { ImpactedAsset } from "../../types/impactReport";
import { industryTransitionKeys } from "../../utils/i18n";

import { IndustryTransitionRow } from "./IndustryTransitionRow";
import { IndustryTransitionsStack, TransitionRowArrowSpacer } from "./styles";

const COMPACT_NUMBER_THRESHOLD = 1000;

type IndustryMappingPreviewProps = {
    readonly asset: ImpactedAsset;
};

const formatDeltaValue = (delta: number): string => {
    const formatted = formatCompactNumber(Math.abs(delta), COMPACT_NUMBER_THRESHOLD);
    const prefix = delta > 0 ? "+" : "-";

    return `${prefix}${formatted}`;
};

export const IndustryMappingPreview = ({ asset }: IndustryMappingPreviewProps) => {
    const { translate } = useTranslate();

    const delta = asset.newCompaniesEstimationDelta;
    const isPositiveDelta = delta !== undefined && delta > 0;

    return (
        <Stack
            gap="12px"
            data-automation="industry-transition-impact-report-row-mapping"
        >
            {delta === undefined ? null : (
                <Stack gap="4px">
                    <Text
                        fontVariant="captionTitle"
                        color={colorsPalettes.carbon[300]}
                    >
                        {translate(
                            isPositiveDelta
                                ? industryTransitionKeys.reportRowMoreCoverage
                                : industryTransitionKeys.reportRowSharperTargeting,
                        )}
                    </Text>
                    <Text
                        fontVariant="subtitle1"
                        color={isPositiveDelta ? colorsPalettes.green[400] : colorsPalettes.blue[400]}
                    >
                        {`${formatDeltaValue(delta)} ${translate(industryTransitionKeys.reportRowCompanies)}`}
                    </Text>
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[300]}
                    >
                        {translate(
                            isPositiveDelta
                                ? industryTransitionKeys.reportRowMoreCoverageDescription
                                : industryTransitionKeys.reportRowSharperTargetingDescription,
                        )}
                    </Text>
                </Stack>
            )}

            <Stack gap="8px">
                <Flex
                    gap="12px"
                    alignItems="center"
                >
                    <Text
                        fontVariant="captionTitle"
                        color={colorsPalettes.carbon[300]}
                        flex="1"
                        minWidth="0"
                    >
                        {translate(industryTransitionKeys.reportRowCurrentIndustry)}
                    </Text>
                    <TransitionRowArrowSpacer />
                    <Text
                        fontVariant="captionTitle"
                        color={colorsPalettes.carbon[300]}
                        flex="1"
                        minWidth="0"
                    >
                        {translate(industryTransitionKeys.reportRowTaxonomy)}
                    </Text>
                </Flex>

                <IndustryTransitionsStack gap="8px">
                    {asset.industries.map((transition, index) => (
                        <IndustryTransitionRow
                            key={`${transition.industry}-${index}`}
                            transition={transition}
                            rowIndex={index}
                        />
                    ))}
                </IndustryTransitionsStack>
            </Stack>
        </Stack>
    );
};
